import { z } from 'zod';

export const loginSchema = z.object({
  email: z.string().email('Invalid email address'),
  password: z.string().min(1, 'Password is required'),
});

export type LoginInput = z.infer<typeof loginSchema>;

export const registerCompanySchema = z
  .object({
    company_name: z.string().min(2, 'Company name is required'),
    subdomain: z
      .string()
      .min(3, 'Subdomain must be at least 3 characters')
      .max(63)
      .regex(/^[a-z0-9-]+$/, 'Only lowercase letters, numbers, and hyphens'),
    admin_name: z.string().min(2, 'Admin name is required'),
    admin_email: z.string().email('Invalid email address'),
    admin_password: z
      .string()
      .min(8, 'Password must be at least 8 characters')
      .regex(/[A-Z]/, 'Must contain uppercase letter')
      .regex(/[a-z]/, 'Must contain lowercase letter')
      .regex(/[0-9]/, 'Must contain a number'),
    admin_password_confirmation: z.string(),
    plan_slug: z.enum(['starter', 'popular', 'advance']),
  })
  .refine((data) => data.admin_password === data.admin_password_confirmation, {
    message: 'Passwords do not match',
    path: ['admin_password_confirmation'],
  });

export type RegisterCompanyInput = z.infer<typeof registerCompanySchema>;

export interface Plan {
  id: string;
  name: string;
  slug: string;
  price_monthly: string;
  price_yearly: string;
  features: string[];
  limits: Record<string, number>;
  is_active: boolean;
}

export interface Tenant {
  id: string;
  name: string;
  subdomain: string;
  portal_url: string;
  status: 'pending' | 'active' | 'suspended' | 'failed' | 'deleted';
  owner_email: string;
  plan?: Plan;
  provisioning_error?: string | null;
  provisioned_at?: string | null;
  enabled_module_slugs?: string[];
  enabled_modules?: string[];
  created_at?: string;
}

export type ModuleCategory =
  | 'crm'
  | 'marketing'
  | 'finance'
  | 'support'
  | 'projects'
  | 'automation'
  | 'analytics';

export type ModulePlanTier = 'starter' | 'popular' | 'advance';

export interface PlatformModule {
  id: string;
  name: string;
  slug: string;
  category: ModuleCategory;
  description?: string | null;
  icon?: string | null;
  laravel_module?: string | null;
  min_plan: ModulePlanTier;
  sort_order: number;
  is_core: boolean;
  is_active: boolean;
  enabled?: boolean;
}

export interface TenantModulesResponse {
  tenant_id: string;
  tenant_name: string;
  plan?: string;
  enabled_module_ids: string[];
  enabled_module_slugs: string[];
  modules: PlatformModule[];
}

export interface ModulesCatalogResponse {
  modules: PlatformModule[];
  by_category: Record<string, PlatformModule[]>;
}

export interface User {
  id: string;
  name: string;
  email: string;
  phone?: string | null;
  avatar?: string | null;
  job_title?: string | null;
  is_active: boolean;
  roles?: string[];
  last_login_at?: string | null;
}

export interface SuperAdmin {
  id: string;
  name: string;
  email: string;
  is_active: boolean;
  last_login_at?: string | null;
}

export interface ApiResponse<T> {
  data: T;
  message?: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  meta: {
    current_page: number;
    per_page: number;
    total: number;
    last_page: number;
  };
}

export interface AuthResponse {
  token: string;
  user: User | SuperAdmin;
  permissions?: string[];
}

export interface TenantStats {
  total: number;
  active: number;
  pending: number;
  suspended: number;
  failed: number;
}

// ─── CRM Types ───────────────────────────────────────────────────────────────

export type LeadStatus =
  | 'new'
  | 'contacted'
  | 'qualified'
  | 'unqualified'
  | 'converted'
  | 'lost';

export type LeadSource =
  | 'website'
  | 'referral'
  | 'cold_call'
  | 'social'
  | 'ad_campaign'
  | 'other';

export interface Lead {
  id: string;
  name: string;
  email: string;
  phone?: string | null;
  company?: string | null;
  job_title?: string | null;
  status: LeadStatus;
  source: string;
  score: number;
  estimated_value?: number | null;
  currency?: string;
  assigned_to?: string | null;
  notes?: string | null;
  converted_at?: string | null;
  created_by?: string;
  created_at?: string;
  updated_at?: string;
}

export interface Contact {
  id: string;
  account_id?: string | null;
  first_name: string;
  last_name: string;
  email: string;
  phone?: string | null;
  mobile?: string | null;
  job_title?: string | null;
  is_primary?: boolean;
  assigned_to?: string | null;
  created_at?: string;
  updated_at?: string;
}

export type AccountType = 'prospect' | 'customer' | 'partner' | 'vendor';

export interface Account {
  id: string;
  name: string;
  type: AccountType;
  industry?: string | null;
  website?: string | null;
  phone?: string | null;
  email?: string | null;
  annual_revenue?: number | null;
  employee_count?: number | null;
  assigned_to?: string | null;
  created_at?: string;
  updated_at?: string;
}

export type DealStatus = 'open' | 'won' | 'lost';

export interface Deal {
  id: string;
  title: string;
  account_id?: string | null;
  contact_id?: string | null;
  pipeline_id: string;
  stage_id: string;
  amount: number;
  currency: string;
  probability: number;
  status?: DealStatus;
  expected_close?: string | null;
  assigned_to?: string | null;
  source?: string | null;
  notes?: string | null;
  won_at?: string | null;
  lost_at?: string | null;
  lost_reason?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface Pipeline {
  id: string;
  name: string;
  is_default: boolean;
  is_active: boolean;
  stages?: PipelineStage[];
  created_at?: string;
}

export interface PipelineStage {
  id: string;
  pipeline_id: string;
  name: string;
  probability: number;
  sort_order: number;
  is_won: boolean;
  is_lost: boolean;
}

export interface PipelineBoardStage extends PipelineStage {
  deals: Deal[];
}

export interface PipelineBoard {
  pipeline: Pipeline;
  stages: PipelineBoardStage[];
}

export type TaskType = 'call' | 'email' | 'meeting' | 'follow_up' | 'demo' | 'other';
export type TaskPriority = 'low' | 'medium' | 'high' | 'urgent';
export type TaskStatus = 'pending' | 'in_progress' | 'completed' | 'cancelled';

export interface Task {
  id: string;
  title: string;
  description?: string | null;
  type: TaskType;
  priority: TaskPriority;
  status: TaskStatus;
  due_date?: string | null;
  assigned_to: string;
  related_type?: string | null;
  related_id?: string | null;
  completed_at?: string | null;
  created_at?: string;
  updated_at?: string;
}

export type MeetingStatus = 'scheduled' | 'in_progress' | 'completed' | 'cancelled';

export interface Meeting {
  id: string;
  title: string;
  description?: string | null;
  location?: string | null;
  meeting_url?: string | null;
  starts_at: string;
  ends_at: string;
  status: MeetingStatus;
  organizer_id: string;
  related_type?: string | null;
  related_id?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface Department {
  id: string;
  name: string;
  description?: string | null;
  parent_id?: string | null;
  manager_id?: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface Employee extends User {
  department_id?: string | null;
  department?: Department | null;
}

export interface Role {
  id: string;
  name: string;
  slug: string;
  description?: string | null;
  is_system: boolean;
  permissions?: Permission[];
  permission_slugs?: string[];
  created_at?: string;
}

export interface Permission {
  id: string;
  name: string;
  slug: string;
  module: string;
  description?: string | null;
}

export interface DashboardStats {
  leads: { total: number; new: number; qualified: number; converted: number };
  deals: { total: number; open: number; won: number; lost: number; pipeline_value: number };
  contacts: { total: number };
  accounts: { total: number };
  tasks: { total: number; due_today: number; overdue: number };
  meetings: { upcoming: number };
  employees: { total: number };
}

export type CallLogDirection = 'inbound' | 'outbound';
export type CallLogOutcome = 'answered' | 'no_answer' | 'busy' | 'voicemail' | 'failed';

export interface CallLog {
  id: string;
  direction: CallLogDirection;
  phone_number: string;
  duration_seconds?: number | null;
  outcome?: CallLogOutcome | null;
  notes?: string | null;
  related_type?: string | null;
  related_id?: string | null;
  user_id: string;
  user?: { id: string; name: string } | null;
  called_at: string;
  created_at?: string;
}

export interface ReportsSummary {
  leads: {
    total: number;
    by_status: Record<string, number>;
    conversion_rate: number;
  };
  deals: {
    total: number;
    open: number;
    won: number;
    lost: number;
    pipeline_value: number;
    by_stage: Array<{ stage: string; count: number; value: number }>;
    win_rate: number;
  };
  tasks: {
    total: number;
    by_status: Record<string, number>;
    completion_rate: number;
  };
  call_logs: {
    total: number;
    this_month: number;
  };
}

export interface ListParams {
  page?: number;
  per_page?: number;
  search?: string;
  status?: string;
  sort?: string;
}

// ─── Zod Schemas ─────────────────────────────────────────────────────────────

export const createLeadSchema = z.object({
  name: z.string().min(2, 'Name is required'),
  email: z.string().email('Invalid email address'),
  phone: z.string().optional(),
  company: z.string().optional(),
  job_title: z.string().optional(),
  source: z.enum(['website', 'referral', 'cold_call', 'social', 'ad_campaign', 'other']).default('website'),
  estimated_value: z.coerce.number().optional(),
  assigned_to: z.string().optional(),
  notes: z.string().optional(),
});

export type CreateLeadInput = z.infer<typeof createLeadSchema>;

export const updateLeadSchema = createLeadSchema.partial().extend({
  status: z.enum(['new', 'contacted', 'qualified', 'unqualified', 'converted', 'lost']).optional(),
});

export type UpdateLeadInput = z.infer<typeof updateLeadSchema>;

export const createContactSchema = z.object({
  first_name: z.string().min(1, 'First name is required'),
  last_name: z.string().min(1, 'Last name is required'),
  email: z.string().email('Invalid email address'),
  phone: z.string().optional(),
  mobile: z.string().optional(),
  job_title: z.string().optional(),
  account_id: z.string().optional(),
  assigned_to: z.string().optional(),
});

export type CreateContactInput = z.infer<typeof createContactSchema>;

export const createAccountSchema = z.object({
  name: z.string().min(2, 'Account name is required'),
  type: z.enum(['prospect', 'customer', 'partner', 'vendor']).default('prospect'),
  industry: z.string().optional(),
  website: z.string().url('Invalid URL').optional().or(z.literal('')),
  phone: z.string().optional(),
  email: z.string().email('Invalid email').optional().or(z.literal('')),
  annual_revenue: z.coerce.number().optional(),
  employee_count: z.coerce.number().optional(),
  assigned_to: z.string().optional(),
});

export type CreateAccountInput = z.infer<typeof createAccountSchema>;

export const createDealSchema = z.object({
  title: z.string().min(2, 'Deal title is required'),
  pipeline_id: z.string().min(1, 'Pipeline is required'),
  stage_id: z.string().min(1, 'Stage is required'),
  amount: z.coerce.number().min(0, 'Amount must be positive'),
  currency: z.string().length(3).default('USD'),
  account_id: z.string().optional(),
  contact_id: z.string().optional(),
  expected_close: z.string().optional(),
  assigned_to: z.string().optional(),
  source: z.string().optional(),
  notes: z.string().optional(),
});

export type CreateDealInput = z.infer<typeof createDealSchema>;

export const createTaskSchema = z.object({
  title: z.string().min(2, 'Title is required'),
  description: z.string().optional(),
  type: z.enum(['call', 'email', 'meeting', 'follow_up', 'demo', 'other']).default('other'),
  priority: z.enum(['low', 'medium', 'high', 'urgent']).default('medium'),
  due_date: z.string().optional(),
  assigned_to: z.string().min(1, 'Assignee is required'),
  related_type: z.string().optional(),
  related_id: z.string().optional(),
});

export type CreateTaskInput = z.infer<typeof createTaskSchema>;

export const createMeetingSchema = z.object({
  title: z.string().min(2, 'Title is required'),
  description: z.string().optional(),
  location: z.string().optional(),
  meeting_url: z.string().url('Invalid URL').optional().or(z.literal('')),
  starts_at: z.string().min(1, 'Start time is required'),
  ends_at: z.string().min(1, 'End time is required'),
});

export type CreateMeetingInput = z.infer<typeof createMeetingSchema>;

export const createCallLogSchema = z.object({
  direction: z.enum(['inbound', 'outbound']),
  phone_number: z.string().min(5, 'Phone number is required'),
  duration_seconds: z.coerce.number().min(0).optional(),
  outcome: z.enum(['answered', 'no_answer', 'busy', 'voicemail', 'failed']).optional(),
  notes: z.string().optional(),
  related_type: z.string().optional(),
  related_id: z.string().optional(),
  user_id: z.string().min(1, 'User is required'),
  called_at: z.string().min(1, 'Call time is required'),
});

export type CreateCallLogInput = z.infer<typeof createCallLogSchema>;

export const createDepartmentSchema = z.object({
  name: z.string().min(2, 'Department name is required'),
  description: z.string().optional(),
  parent_id: z.string().optional(),
  manager_id: z.string().optional(),
});

export type CreateDepartmentInput = z.infer<typeof createDepartmentSchema>;

export const createEmployeeSchema = z.object({
  name: z.string().min(2, 'Name is required'),
  email: z.string().email('Invalid email address'),
  password: z.string().min(8, 'Password must be at least 8 characters'),
  phone: z.string().optional(),
  job_title: z.string().optional(),
  department_id: z.string().optional(),
  role_ids: z.array(z.string()).optional(),
});

export type CreateEmployeeInput = z.infer<typeof createEmployeeSchema>;
